<template>
  <div v-if="siteComponents">
    <b-modal
      :id="`modal-add-page${custom_id}`"
      class="modal-add-page"
      :header-class="`align-items-center px-20 ${!pageType ? 'py-15' : 'bt-15 pb-0'}`"
      body-class="p-20"
      footer-class="px-20 py-15"
      no-close-on-esc
      no-close-on-backdrop
      :hide-footer="!pageType"
      @hidden="clear()"
      @shown="setTemplate()"
    >
      <template #modal-header>
        <div class="w-100">
          <div class="d-flex justify-content-between align-items-center">
            <div class="modal-title">
              <span class="backIcon cursor-pointer mr-10" @click="isSaving ? false : backToTemplate()">
                <i class="fal fa-arrow-left fs-16"></i>
              </span>
              <TitleModal 
                :label="
                  action == 'add'
                    ? $t('engine.managePage.pageAddEdit.lblTitleAdd', languageEngine)
                    : $t('engine.managePage.pageAddEdit.lblTitleEdit', languageEngine)
                " 
              />
            </div>
            <div
              id="close-modal-manage-header-menu"
              type="button"
              class="close-modal"
              :class="{ 'text-muted': isSaving }"
              aria-label="Close"
              @click="isSaving ? false : close()"
            >
              <i class="fal fa-times"></i>
            </div>
          </div>

          <b-row class="mt-15">
            <b-col sm="4">
              <label class="fs-sa-14 pt-7" for="btn-select-page">
                {{ $t("engine.managePage.pageAddEdit.lblPageType", languageEngine) }}
              </label>
            </b-col>
            <b-col sm="8">
              <NormalSelect
                class="mb-5"
                :optionGroup="true"
                :selected="pageType"
                :options="pagesUsedFetching === true && action == 'add' ? [] : pageTypes"
                :optionsDisabled="pagesUsed.system.map((page) => page.type)"
                :optionValue="'value'"
                :optionLabel="'name'"
                :selectedDefault="'section'"
                :notMatchText="
                  pagesUsedFetching === true && action == 'add'
                    ? $t('engine.common.lblLoading', languageEngine)
                    : $t('engine.managePage.pageAddEdit.lblChoosePageType', languageEngine)
                "
                :conditions="[
                  {
                    key: 'disabled',
                    matchValue: true,
                    content: `<span class=\'ml-10\'>(${$t('engine.managePage.pageManage.lblPageUsed', languageEngine)})</span>`,
                  },
                ]"
                :readonly="true"
              />
            </b-col>
          </b-row>

          <b-tabs
            v-if="pageType !== undefined"
            v-model="tabIndex"
            class="custom-tabs fs-sa-14 mt-20 -mb-1"
            content-class="d-none"
            align="center"
          >
            <template #tabs-end>
              <b-tab :title="$t('engine.managePage.pageAddEdit.info.lblTab', languageEngine)"></b-tab>
              <!-- <b-tab :title="$t('engine.managePage.pageAddEdit.seo.lblTab', languageEngine)"></b-tab> -->
              <b-tab :title="$t('engine.managePage.pageAddEdit.permission.lblTab', languageEngine)"></b-tab>
            </template>
          </b-tabs>
        </div>
      </template>
      
      <b-overlay variant="white" :opacity="1" :show="isSaving">
        <div v-if="pageType !== undefined">
          <b-form>
            <b-tabs v-model="tabIndex" nav-class="d-none" content-class="fs-sa-14 p-0 mt-15">
              <b-tab>
                <component
                  v-if="pageType"
                  :is="`page_${pageType}`"
                  :action="action"
                  :data="data"
                  :sectionPage="sectionPage"
                  :validate="$v"
                  :dataFetching="dataFetching"
                  :sectionPageFetching="sectionPageFetching"
                  :pagesUsed="pagesUsed"
                  :noHeader="noHeader"
                  @changeUrl="onChangeUrl"
                  :urlStatus="urlStatus"
                ></component>
                <div v-else class="text-center">Coming Soon</div>
              </b-tab>
              <!-- <b-tab>
                <SEOSetting
                  v-if="data"
                  :data="data"
                  :validate="$v.data.properties.seo.keywords"
                />
              </b-tab> -->
              <b-tab>
                <PermissionSetting
                  v-if="data"
                  :data="data"
                  :validate="$v"
                />
              </b-tab>
            </b-tabs>
          </b-form>
        </div>
        <div v-else>
          <div class="text-hint text-center fs-sa-14">
            <span>{{ $t("engine.managePage.pageAddEdit.msgChoosePageType", languageEngine) }}</span>
          </div>
        </div>

        <template #overlay>
          <Loading :title="isSaving ? false : true" />
        </template>
      </b-overlay>

      <template #modal-footer>
        <b-button variant="blue" @click.prevent="save($event)" :disabled="btnSaveDisable">
          <i class="far fa-check"></i>
          {{ $t("engine.managePage.pageAddEdit.btnSave", languageEngine) }}
        </b-button>
        <b-button variant="default" @click="close()" :disabled="isSaving">
          <i class="far fa-times"></i>
          {{ $t("engine.managePage.pageAddEdit.btnCancel", languageEngine) }}
        </b-button>
      </template>
    </b-modal>
    
    <ModalConfirmUpgrade 
      v-if="pageLimit && action == 'add'"
      :modalID="'modal-confirm-upgrade-addpage'"
      :title="$t('engine.common.dialogAlertLimit.lblTitlePageLimit',languageEngine)"
      :message="$t('engine.common.dialogAlertLimit.lblDescPageLimit',languageEngine)"
      @clear="backToTemplate"
    />
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import Loading from "@components_system/Shared/Tools/Loading/Loading";
import { getPageGroupTypes, getPageUrlReserve } from "@model/masterData";
import PermissionSetting from "./Permission/PermissionSetting";
import { capitalize } from "@helpers/text.helper";
import pageModel from "@model/components/page.model";
import headerMenuModel from "@model/components/headerMenu.model";
import siteService from "@services/siteService.js";
import pageService from "@services/pageService.js";
import { required, maxLength } from "vuelidate/lib/validators";
import { HEADER_MENU } from "@model/components/componentMeta.model";
import { genUniqKey } from "@helpers/component.helper";
import NormalSelect from "@components_system/Shared/Tools/NormalSelect/NormalSelect";
import TitleModal from "@components_system/Shared/Elements/Texts/TitleModal";
import presetService from "@services/presetService.js";
import { mock2Page, replaceThemeWebsite} from "@helpers/component.helper";
import ModalConfirmUpgrade from "@components_system/Design/Modal/ModalConfirmUpgrade/ModalConfirmUpgrade.vue";

const InfoSettingViews = {
  page_section: () => import(/* webpackChunkName: "page_Section" */ "./Info/InfoSetting"),
  page_product_list: () => import(/* webpackChunkName: "page_product_list" */ "./Info/InfoSettingPageProduct"),
  page_content_list: () => import(/* webpackChunkName: "page_content_list" */ "./Info/InfoSettingPageContent"),
  page_content_item: () => import(/* webpackChunkName: "page_content_item" */ "./Info/InfoSettingPageContentItem"),
  page_product_item: () => import(/* webpackChunkName: "page_product_item" */ "./Info/InfoSettingPageProductItem.vue"),
  page_form_item: () => import(/* webpackChunkName: "page_form_item" */ "./Info/InfoSettingPageFormItem.vue"),
  page_product_brand: () => import(/* webpackChunkName: "page_product_brand" */ "./Info/InfoSettingPageProductBrand.vue"),
  page_giftcard_list: () => import(/* webpackChunkName: "page_giftcard_list" */ "./Info/InfoSettingPageGiftCard.vue"),
  page_album_list: () => import(/* webpackChunkName: "page_album_list" */ "./Info/InfoSettingPageAlbum"),
  page_album_item: () => import(/* webpackChunkName: "page_album_item" */ "./Info/InfoSettingPageAlbumItem"),
  page_terms_condition: () => import(/* webpackChunkName: "page_terms_condition" */ "./Info/InfoSettingPageTermsCondition"),
  page_contact: () => import(/* webpackChunkName: "page_contact" */ "./Info/InfoSettingPageContact"),
  page_faq: () => import(/* webpackChunkName: "page_faq" */ "./Info/InfoSettingPageFAQ.vue"),
  page_forum_list: () => import(/* webpackChunkName: "page_forum_list" */ "./Info/InfoDefault"),
  page_inform_payment: () => import(/* webpackChunkName: "page_inform_payment" */ "./Info/InfoSettingPageInformpayment"),
  page_online_payment: () => import(/* webpackChunkName: "page_online_payment" */ "./Info/InfoSettingPageOnlinePayment"),
  page_order_tracking: () => import(/* webpackChunkName: "page_order_tracking" */ "./Info/InfoSettingPageOrderTracking"),
  page_event_calendar: () => import(/* webpackChunkName: "page_event_calendar" */ "./Info/InfoSettingPageEventCalendar"),
  page_policy: () => import(/* webpackChunkName: "page_policy" */ "./Info/InfoSettingPagePolicy"),
  page_search: () => import(/* webpackChunkName: "page_search" */ "./Info/InfoSettingPageSearch"),
  page_quotation_form_item: () => import(/* webpackChunkName: "page_quotation_form_item" */ "./Info/InfoSettingPageQuotationFormItem"),
};

export default {
  components: {
    ...InfoSettingViews,
    PermissionSetting,
    NormalSelect,
    Loading,
    TitleModal,
    ModalConfirmUpgrade,
  },
  props: {
    id: {
      type: String,
      default: "",
    },
    action: {
      type: String,
      default: "add", // add, edit
    },
    custom_id: {
      type: String,
      default: "",
    },
    custom_open: {
      type: String,
      default: "modal-manage-page",
    },
    templateData : {
      type : Object,
      default : () => {}
    },
    noHeader : {
      type : Boolean,
      default : false
    }
  },
  data() {
    return {
      tabIndex: 0,
      data: null,
      sectionPage: null,
      dataFetching: false,
      sectionPageFetching: false,
      createMenu: true,
      urlStatus: "none",
      urlOld: null,
      isSaving: false,
      isSuggestUrl: false,
      pageTypeTemp: 'section',
      pagesUsedFetching: false,
      pagesUsed: {
        system: [],
        item: [],
      },
      pagesUsedTypes: {
        system: [
          "contact",
          "online_payment",
          "inform_payment",
          "terms_condition",
          "product_brand",
          "order_tracking",
          "event_calendar",
          "policy",
          "search",
        ],
        item: [
          "content_list",
          "content_item",
          "album_list",
          "album_item",
          "form_item",
          "quotation_form_item",
          "product_list",
          "product_item",
          "faq",
          "giftcard_list",
        ],
      },
      clicked : false,
    };
  },
  computed: {
    ...mapGetters({
      languageUse: "design/site/languageUse",
      languageEngine: "design/userPreference/languageEngine",
      siteComponents: "design/metadata/siteComponents",
      sitePageID: "design/metadata/sitePageID",
      components: "design/metadata/components",
      metaDataID: "design/metadata/metaDataID",
      appearance :"design/metadata/appearance",
      appearanceLanguage : "design/metadata/appearanceLanguage",
      design: "design/site/design",
      domains: "design/site/domains",
      permissions: "design/permissionWebsite/permissions",
      services: "design/page/services",
    }),
    pageLimit() {
      return this.action == 'add' && this.pageType == "section" && this.permissions.PageNumber >= 9999 ? false : this.action == 'add' && this.pageType == "section" ? this.pageNumber >= this.permissions.PageNumber : false;
    },
    pageNumber() {
      const temp = JSON.parse(JSON.stringify(this.siteComponents));
      return Object.keys(temp).filter(item => temp[item].type == 'PAGE' && temp[item].properties.type == 'section').length || 0;
    },
    domainDisplay() {
      return this.domains.main;
    },
    pageTypes() {
      return getPageGroupTypes(this.languageEngine);
    },
    pageType: {
      get() {
        return (this.data && this.data.properties.type) || this.pageTypeTemp
      },
      set(value) {
        if (this.data) {
          this.data.properties.type = value;
        }
      },
    },
    pageUrl: {
      get() {
        return (this.data && this.data.properties.url) || "";
      },
      set(value) {
        if (this.data) {
          this.data.properties.url = value;
        }
      },
    },
    tabError() {
      if (this.tabIndex == 0) return this.$v.form.infoSetting.$error;
      else if (this.tabIndex == 1) return this.$v.form.passwordSetting.$error;
      return false;
    },
    btnSaveDisable() {
      return this.tabError || this.urlStatus == 'checking_' || this.isSaving;
    },
    reserveUrls() {
      return getPageUrlReserve();
    }
  },
  methods: {
    capitalize,
    setTemplate() {
      if(this.templateData && Object.keys(this.templateData) && this.action == 'add') {
        this.data = null;
        this.pageTypeTemp = this.templateData.activeTypeTabs == 'custom' ? 'section' : this.templateData.activeTabs;
        this.onChangePageType(this.pageTypeTemp);
      }
      if(this.action == 'add' && this.pageLimit) {
        this.$bvModal.show('modal-confirm-upgrade-addpage');
      }
    },
    async init() {
      if (this.action == "add") {
        this.fetchPagesUsed();
        this.tabIndex = 0,
        this.isSuggestUrl = true;
        this.data = null;
        this.sectionPage = null;
        this.createMenu = true;
        this.urlStatus = "none";
        this.clicked = false;
        this.urlOld = this.pageUrl;
        this.$v.$reset();
      } else {
        this.fetchPagesUsed();
        this.tabIndex = 0,
        this.isSuggestUrl = false;
        this.data = this.getData(this.id);
        this.sectionPageFetching = true;
        this.sectionPage = await this.getSectionPage();
        this.sectionPageFetching = false;
        this.createMenu = true;
        this.urlStatus = "none";
        this.clicked = false;
        this.urlOld = this.pageUrl;
        this.$v.$reset();
      }
    },
    clear() {
      this.tabIndex = 0,
      this.data = null,
      this.sectionPage = null,
      this.dataFetching = false,
      this.sectionPageFetching = false,
      this.createMenu = true,
      this.urlStatus = "none",
      this.urlOld = null,
      this.isSaving = false,
      this.isSuggestUrl = false,
      this.pagesUsed = {
        system: [],
        item: [],
      },
      this.pagesUsedFetching = false,
      this.$v.$reset();
    },

    // Pages Used
    async fetchPagesUsed() {
      this.pagesUsedFetching = true;
      let response = await pageService.getPagesUsed({
        filterType: "language",
        language: this.languageUse,
      });
      this.pagesUsed = {
        system: Object.values(response)[1].filter((ele) =>
          this.pagesUsedTypes.system.includes(ele.type)
        ),
        item: Object.values(response)[1].filter((ele) =>
          this.pagesUsedTypes.item.includes(ele.type)
        ),
      };
      this.pagesUsedFetching = false;
    },

    // Fetch
    getData(id) {
      this.dataFetching = true;
      let data = {
        ...JSON.parse(JSON.stringify(this.siteComponents[id])),
      };
      this.dataFetching = false;
      return data;
    },
    async getSectionPage() {
      if (this.pageType == "section") {
        // page section
        return null;
      } else if (this.id == this.sitePageID) {
        // page current
        return JSON.parse(JSON.stringify(this.components["SECTION_PAGE"]));
      } else {
        // page other
        if (this.languageUse && this.id) {
          const response = await siteService.getSitePageComponent({
            lang: this.languageUse,
            pageID: this.data.properties.metaDataID,
            componentID: "SECTION_PAGE",
          });
          return JSON.parse(JSON.stringify(response));
        }
      }
    },

    // Gen
    genData(pageID = null, pageType = null, customComponent = {}) {
      this.dataFetching = true;
      let data = pageModel.createSitePage(
        pageID,
        pageType,
        customComponent,
        this.siteComponents
      );
      this.dataFetching = false;
      return data;
    },
    async genPageContent(presetID) {
      // this.$store.dispatch('design/blockloading', {}, { root: true })
      let keyTemp = '';
      let objectTemp = '';
      await presetService.getPresetRender({ id : presetID, metaName : 'other', languageUse: this.languageUse, metaDataID:this.metaDataID}).then((response) => {
        let { key , object } = mock2Page(
          this.siteComponents,
          this.components,
          response
        );
        object = replaceThemeWebsite(object, this.appearance, this.appearanceLanguage);
        keyTemp = key;
        objectTemp = object;
        // this.$store.dispatch('design/unblockloading', {}, { root: true })
      });
      return { key:keyTemp , object : objectTemp};
    },
    // genPageContent(pageID = null, pageType = null, customComponent = {}) {
    //   return pageModel.createPage(
    //     pageID,
    //     pageType,
    //     customComponent,
    //     this.siteComponents
    //   );
    // },
    genSectionPage(pageType = null, customComponent = {}) {
      this.sectionPageFetching = true;
      let sectionPage = pageModel.createSectionPage(pageType, customComponent);
      this.sectionPageFetching = false;
      return sectionPage;
    },

    // Event
    onChangePageType(pageType) {
      if (this.action == "add") {
        // add only
        let customCmp = {};
        if (this.data) {
          customCmp = {
            properties: {
              name: this.data.properties.name,
              seo: this.data.properties.seo,
              permission: this.data.properties.permission,
            },
          };
        }

        this.isSuggestUrl = true;
        this.tabIndex = 0;
        this.data = this.genData(null, pageType, customCmp);
        this.sectionPage = this.genSectionPage(pageType);
        this.urlOld = this.pageUrl;
        this.$v.$reset();
      }
    },
    onChangeUrl(value) {
      this.urlStatus = "none";
      this.pageUrl = value;
    },

    close(isManage = false) {
      if (this.$route.query.redirect_wb) {
        this.redirectToWb();
      } else {
        if(this.action == "add" && !isManage) {
          this.backToTemplate();
        }else {
          this.$bvModal.hide(`modal-add-page${this.custom_id}`);
          this.$bvModal.show(this.custom_open);
          this.$emit("clear");
        }
      }
    },
    backToTemplate() {
      if (this.action == "add") {
        this.$bvModal.hide(`modal-add-page${this.custom_id}`);
        this.$bvModal.show(`modal-select-template-page${this.custom_id}`);
        this.$emit("clear");
      }else {
        this.close();
      }
    },
    async save(evt) {
      this.clicked = true;
      evt.preventDefault();
      this.isSaving = true;

      // Validations
      this.$v.$touch();
      if (this.$v.form.$invalid) {
        let invalidTabs = [];
        if (this.$v.form.infoSetting.$invalid && !invalidTabs.includes(0)) {
          invalidTabs.push(0);
        }
        if (this.$v.form.passwordSetting.$invalid && !invalidTabs.includes(1)) {
          invalidTabs.push(1);
        }
        if (!invalidTabs.includes(this.tabIndex)) {
          this.tabIndex = invalidTabs[0]; // if not current, active tab
        }
        this.isSaving = false;
        return;
      }

      // Add, Edit
      await this.$store.dispatch("design/snapshot/synceSnapshot" , { });
      if (this.action == "add") {
        await this.addPage();
      } else {
        await this.editPage();
      }
      if (this.$route.query.redirect_wb) {
        this.redirectToWb();
      }
      
      return;
    },
    async addPage() {
      const language = this.languageUse;
      const pageType = this.pageType;
      // const pageID = genUniqKey(this.siteComponents, PAGE.prefixKey);
      const data = this.data;
      const sectionPage = this.sectionPage;
      const createMenu = this.createMenu;
      // let pageData = this.genData(pageID, pageType); // SITE > PAGE
      // let pageContent = this.genPageContent(pageID, pageType); // PAGE
      let { key , object } = await this.genPageContent(this.templateData.templateID); // PAGE
      const pageID = key;
      let pageContent = object;
      let pageData = this.genData(pageID, pageType); // SITE > PAGE
      let snapshots = [];

      // 1. Page
      if (pageType == "section") {
        pageData.properties = data.properties;
      } else {
        pageData.properties = data.properties;
        pageContent.assets.components.SECTION_PAGE.data = sectionPage.data;
      }

      // if (pageType == "section") {
        // pageData.properties = data.properties;
      //   pageContent.props.components.CONTENT_CONTAINER.componentsID = [];
      //   pageContent.assets.components = {};
      // } else {
        // pageData.properties = data.properties;
        // pageContent.assets.components.SECTION_PAGE.data = sectionPage.data;
      // }
      snapshots.push(
        {
          op: "add",
          path: `/languages/${language}/SITE/assets/components/${pageID}`,
          value: pageData,
        },
        {
          op: "add",
          path: `/languages/${language}/pages/${pageID}`,
          value: pageContent,
        }
      );

      // 2. Menu
      if (createMenu === true && !this.noHeader) {
        const menuID = genUniqKey(this.siteComponents, HEADER_MENU.prefixKey);
        const lastIndex = this.siteComponents.HEADER_WIDGET_MENU.componentsID.length;
        let cmpMenu = headerMenuModel.create(menuID, {
          properties: {
            name: pageData.properties.name,
            link: {
              type: "page",
              urlIsEnable: false,
              url: null,
              pageID: pageData.id,
              sectionPageID: null,
              sectionID: null,
              target: "_self",
            },
          },
        });
        snapshots.push(
          {
            op: "add",
            path: `/languages/${language}/SITE/assets/components/HEADER_WIDGET_MENU/componentsID/${lastIndex}`,
            value: `${menuID}`,
          },
          {
            op: "add",
            path: `/languages/${language}/SITE/assets/components/${menuID}`,
            value: cmpMenu,
          }
        );
      }

      // Process
      this.setLogsLocal(snapshots);
      const response = await pageService.managesPage({ data: { actions: snapshots, transactionID: localStorage.getItem('lastTransactionID') } });
      const metaDataID = response.result.pagesMetaDataID[language][pageID];
      localStorage.setItem('lastTransactionID', response.transactionID)
      
      // Load Page
      await this.$store.dispatch("designIframe/page/getPage", { metaDataID: metaDataID });
      
      this.isSaving = false;
      this.$toast.success({ title: this.$t("engine.common.alert.success.title", this.languageEngine), message: this.$t("engine.managePage.pageAddEdit.msgAddSuccess", this.languageEngine) });
      this.$emit("pagesaved", pageID);
      this.$emit("scrollToPage", pageID);
      await this.$store.dispatch("design/snapshot/synceSnapshot" , { });
      this.close(true);
    },
    async editPage() {
      const language = this.languageUse;
      const pageType = this.pageType;
      const pageID = this.id;
      const pageData = this.data; // SITE > PAGE
      const sectionPage = this.sectionPage; // PAGE > SectionPage

      // Process
      if (pageID == this.sitePageID) {
        // page current
        // 1. Update SITE > PAGE
        this.siteComponents[this.id] = {
          ...JSON.parse(JSON.stringify(pageData)),
        };
        // 2. StorePage > setPageName
        this.$store.dispatch("design/page/setPageName", {
          pageName: pageData.properties.name,
        });
        // 3. Update PAGE > SectionPage
        if (pageType != "section") {
          this.components.SECTION_PAGE = {
            ...JSON.parse(JSON.stringify(sectionPage)),
          };
        }
      } else {
        // page other
        // 1. Update SITE > PAGE
        this.siteComponents[this.id] = {
          ...JSON.parse(JSON.stringify(pageData)),
        };
        // 2. Update PAGE > SectionPage
        if (pageType != "section") {
          const snapshots = [
            {
              op: "replace",
              path: `/languages/${language}/pages/${pageID}/assets/components/SECTION_PAGE`,
              value: sectionPage,
            },
          ];
          const response = await pageService.managesPage({ data: { actions: snapshots, transactionID: localStorage.getItem('lastTransactionID') } });
          localStorage.setItem('lastTransactionID', response.transactionID)
        }
      }
      // this.$store.dispatch("design/snapshot/genSnapshot");

      this.$store.dispatch("design/snapshot/genSnapshot");
      await this.$store.dispatch("design/snapshot/synceSnapshot" , { });
      this.$toast.success({ title: this.$t("engine.common.alert.success.title", this.languageEngine), message: this.$t("engine.managePage.pageAddEdit.msgEditSuccess", this.languageEngine) });
      this.isSaving = false;
      this.close(true);
    },
    setLogsLocal(snapshot) {
      const timeElapsed = Date.now();
      const today = new Date(timeElapsed);
      const date = today.toUTCString();
      
      const newLog = { [timeElapsed] : {
        date : date,
        data : JSON.stringify(snapshot)}
      }
      
      let oldLog = JSON.parse(localStorage.getItem(`LOG_${this.domainDisplay}`));
      if(oldLog) {
        Object.keys(oldLog).map(key => {
          const day = Math.floor((timeElapsed - key) / (1000*60*60*24))
          if(day >= 2) {
            delete oldLog[key];
          }
        })
      }

      const tempLog = {...oldLog,...newLog};

      localStorage.setItem(`LOG_${this.domainDisplay}`,JSON.stringify(tempLog));
    },
    redirectToWb() {
      window.location.href = this.services.webbuilderCmsUrl+`?${this.$route.query.redirect_wb}`;
    }
  },
  watch: {
    action(valueNew) {
      if (valueNew == "add" || valueNew == "edit") {
        this.init();
      }
    },
  },
  validations: {
    data: {
      properties: {
        name: {
          required,
          maxLength: maxLength(50),
        },
        url: {
          required(value) {
            if (this.pageType !== "section") return true; // not url custom
            if (value) {
              return true;
            }
            return false;
          },
          maxLength(value) {
            if (this.pageType !== "section") return true; // not url custom
            if (!this.$v.data.properties.url.required) return true;
            if (value.toLowerCase().length <= 100) {
              return true;
            }
            return false;
          },
          alphaUrl(value) {
            if (this.pageType !== "section") return true; // not url custom
            if (!this.$v.data.properties.url.required) return true;
            if (!this.$v.data.properties.url.maxLength) return true;
            const temp = this.$helper.url.urlCustomerCase(value);
            if (value === temp) {
              return true;
            }
            return false;
          },
          checkUrlChanged(value) {
            if (this.pageType !== "section") return true; // not url custom
            if (!this.$v.data.properties.url.required) return true;
            if (!this.$v.data.properties.url.maxLength) return true;
            if (!this.$v.data.properties.url.alphaUrl) return true;
            if (this.isSuggestUrl === false && value.startsWith('-')) {
              return false;
            }
            return true;
          },
          reserveUrl(value) {
            if (this.pageType !== "section") return true; // not url custom
            if (!this.$v.data.properties.url.required) return true;
            if (!this.$v.data.properties.url.maxLength) return true;
            if (!this.$v.data.properties.url.alphaUrl) return true;
            if (!this.$v.data.properties.url.checkUrlChanged) return true;

            if (this.reserveUrls.includes(value)) return false;
            return true;
          },
          async isUnique(value) {
            if (this.pageType !== "section") return true; // not url custom
            if (!this.$v.data.properties.url.required) return true;
            if (!this.$v.data.properties.url.maxLength) return true;
            if (!this.$v.data.properties.url.alphaUrl) return true;
            if (!this.$v.data.properties.url.checkUrlChanged) return true;
            if (!this.$v.data.properties.url.reserveUrl) return true;
            if (this.action === "edit" && this.urlOld === value) return true; // not change

            this.urlStatus = "checking";
            const url = value;
            const language = this.languageUse;
            const response = await pageService.checkUrlDuplicate({
              language,
              url,
            });

            if (response.isAvailable) {
              this.urlStatus = "none";
              if(this.clicked && this.action === "edit") {
                this.clicked = false;
                this.isSaving = true;
                this.editPage();
              }
              return true;
            } else {
              if (this.isSuggestUrl === true) {
                this.pageUrl = response.suggest;
                this.isSuggestUrl = false;
                this.urlStatus = "none";
                return true;
              }
              this.clicked = false;
              this.urlStatus = "none";
              return false;
            }
          },
        },
        permission: {
          type: {
            required,
          },
          password: {
            required(value) {
              if (this.$v.data.properties.permission.type.$model != 'password') return true;
              if (value) {
                return true;
              }
              return false;
            },
            maxLength(value) {
              if (this.$v.data.properties.permission.type.$model != "password") return true;
              if (value.toString().toLowerCase().length <= 20) {
                return true;
              }
              return false;
            },
            format(value) {
              if (this.$v.data.properties.permission.type.$model != "password") return true;
              return /^[a-z|A-Z|0-9]+$/g.test(value);
            },
          },
          memberType: {
            required(value) {
              if (this.$v.data.properties.permission.type.$model != 'member') return true;
              if (value) {
                return true;
              }
              return false;
            },
          },
          memberGroupsID: {
            custom(value) {
              if (this.$v.data.properties.permission.type.$model != "member") return true;
              if (this.$v.data.properties.permission.memberType.$model != "groups") return true;
              if (value.length > 0) {
                return true;
              }
              return false;
            },
          },
        },
      },
    },
    sectionPage: {
      data: {
        type: {
          custom(value) {
            if (this.pageType == 'product_list' && !["category", "brand", "best_seller", "flash_sale", "promotion"].includes(value)) return false;
            // if (this.pageType == 'policy' && !["privacy", "cookie"].includes(value)) return false;
            return true;
          },
        },
        categoryID: {
          custom(value) {
            if (["album_list", "album_item", "giftcard_list"].includes(this.pageType) && (value === null || value === undefined)) return false;
            if (["product_list", "product_item", "content_list", "content_item", "faq" , "giftcard_list"].includes(this.pageType) && !value) return false;
            if (["product_list", "product_item", "content_list", "content_item"].includes(this.pageType) && value && (value.level1 === null || value.level1 === undefined)) return false;
            return true;
          },
        },
        brandID: {
          custom(value) {
            if (this.pageType == 'product_list' && this.sectionPage.data.type == 'brand' && (value === undefined || value === null || value == 0)) return false;
            return true;
          },
        },
        promotionID: {
          custom(value) {
            if (this.pageType == 'product_list' && this.sectionPage.data.type == 'promotion' && (value === undefined || value === null)) {
              return false;
            }
            return true;
          },
        },
        promotionType: {
          custom(value) {
            if (this.pageType == 'product_list' && this.sectionPage.data.type == 'promotion' && (value === undefined || value === null || !['discount', 'shipping', 'free', 'installment_zero'].includes(value))) return false;
            return true;
          },
        },
        sortBy: {
          custom(value) {
            if (this.pageType == 'product_list' && this.sectionPage.data.type == 'best_seller' && (value === undefined || value === null)) return false;
            return true;
          },
        },
        dateRange: {
          custom(value) {
            if (this.pageType == 'product_list' && this.sectionPage.data.type == 'best_seller' && (value === undefined || value === null)) return false;
            return true;
          },
        },
        albumID: {
          custom(value) {
            if (this.pageType == 'album_item' && (value === undefined || value === null)) return false;
            return true;
          },
        },
        contentID: {
          custom(value) {
            if (this.pageType == 'content_item' && (value === undefined || value === null)) return false;
            return true;
          },
        },
        productID: {
          custom(value) {
            if (this.pageType == 'product_item' && (value === undefined || value === null)) return false;
            return true;
          },
        },
        formID: {
          custom(value) {
            if (this.pageType == 'form_item' && (value === undefined || value === null)) return false;
            return true;
          },
        },
        quotationFormId: {
          custom(value) {
            if (this.pageType == 'quotation_form_item' && (value === undefined || value === null)) return false;
            return true;
          },
        },
        policyType: {
          custom(value) {
            if (this.pageType == 'policy' && !["privacy", "cookie"].includes(value)) return false;
            // if (this.pageType == 'policy' && !["privacy", "cookie"].includes(value)) return false;
            return true;
          },
        },
      },
    },
    createMenu: {
      required,
    },
    form: {
      infoSetting: [
        "data.properties.name",
        "data.properties.url",
        "sectionPage.data",
      ],
      passwordSetting: [
        "data.properties.permission.type",
        "data.properties.permission.password",
        "data.properties.permission.memberType",
        "data.properties.permission.memberGroupsID",
      ],
    },
  },
};
</script>

<style lang="scss">
#modal-add-page-top-header .backIcon,
#modal-add-page-header .backIcon,
#modal-add-page-footer .backIcon,
#modal-add-page-sidebar .backIcon {
  display: none;
}
.modal-header {
  label {
    font-size: 14px;
    font-family: Sarabun;
    line-height: 22px;
  }
}
#btn-select-page-type {
  font-size: 14px;
  font-family: Sarabun;
  line-height: 22px;
  span {
    font-size: 14px;
    font-family: Sarabun;
    line-height: 22px;
  }
  ul {
    border: 0px;
    padding: 0;
    box-shadow: 0px 10px 15px -3px rgba(0, 0, 0, 0.1);
    header {
      background-color: #f8f9fa;
      padding: 2px 20px;
    }
    button {
      font-size: 14px;
      font-family: Sarabun;
      line-height: 22px;
    }
  }
}
#page-keyword___duplicate_feedback__ {
  display: none;
}
/* .custom-tabs .nav-item .nav-link.active::after */
.custom-tabs {
  .nav-item {
    .nav-link {
      position: relative;
      border: unset;
      color: var(--paragraph);
      padding: 0.5rem 2rem 0.7rem 2rem;
      border-bottom: 1px solid #dee2e6;
      &:hover {
        color: var(--primary);
      }
      &::after {
        position: absolute;
        content: "";
        width: 100%;
        height: 4px;
        background-color: var(--primary);
        bottom: 0;
        left: 0;
        /* transition: all 0.2s ease; */
        transition: unset;
        right: 0;
        margin: auto;
        opacity: 0;
      }
      animation: fadeOut 0.15s linear;
      &.active {
        color: var(--primary);
        &::after {
          opacity: 1;
          animation: fade 0.15s linear;
        }
      }
    }
  }
}

@keyframes fade {
  0% {
    opacity: 0;
  }
  100% {
    opacity: 1;
  }
}
@keyframes fadeOut {
  0% {
    opacity: 1;
  }
  100% {
    opacity: 0;
  }
}
</style>